/**
 * ReceiptPreviewModal Component
 * 
 * This component provides a modal for previewing uploaded receipts/documents.
 * It supports image files (JPG, PNG, GIF) and PDF files with appropriate viewers.
 * Falls back to download link for unsupported file types.
 * 
 * Features:
 * - Image preview for common formats
 * - PDF preview via iframe
 * - Download fallback for unsupported types
 * - Loading states and error handling
 * - Full internationalization support (Arabic/English)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';

const ReceiptPreviewModal = ({ receiptUrl, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const isImage = receiptUrl?.toLowerCase().includes('.jpg') || 
                  receiptUrl?.toLowerCase().includes('.jpeg') || 
                  receiptUrl?.toLowerCase().includes('.png') || 
                  receiptUrl?.toLowerCase().includes('.gif');

  const isPDF = receiptUrl?.toLowerCase().includes('.pdf');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">{t('expenses.receipt_preview', 'Receipt Preview')}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 max-h-[80vh] overflow-auto">
          {error ? (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-4">
                {t('expenses.unsupported_file_type', 'Preview not available for this file type')}
              </div>
              <a href={receiptUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                {t('expenses.try_download', 'Try downloading instead')}
              </a>
            </div>
          ) : isImage ? (
            <img
              src={receiptUrl}
              alt="Receipt"
              className="w-full h-auto"
              onLoad={() => setLoading(false)}
              onError={() => { setLoading(false); setError(true); }}
            />
          ) : isPDF ? (
            <iframe
              src={receiptUrl}
              className="w-full h-96"
              title="Receipt PDF"
              onLoad={() => setLoading(false)}
              onError={() => { setLoading(false); setError(true); }}
            />
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-4">
                {t('expenses.unsupported_file_type', 'Preview not available for this file type')}
              </div>
              <a href={receiptUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                {t('common.download', 'Download')}
              </a>
            </div>
          )}
          
          {loading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptPreviewModal;
